import type {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import {
  makeApiProvider,
  useApiQuery,
  useApiQueryNoRefetchOverride,
} from '@pflegenavi/shared-frontend/platform';
import type {
  AvailableFundsDetails,
  BalanceChange,
  BookkeepingChange,
  CashListBalanceChange,
  FinanceResult,
  GetDashboardParams,
  GetDashboardResult,
  GetPayinsParams,
  GetPayinsResult,
  GetPayoutReconciliationParams,
  GetPayoutReconciliationResult,
  GetPayoutsParams,
  GetPayoutsResult,
  GetReportingOverviewParams,
  GetReportingOverviewResult,
  IReportingPhoenixApi,
  PayinFilters,
  PayinSortColumn,
  PayinStatusFilter,
  PayoutReconciliationEntry,
  PayoutType,
  ReceiptStatusCount,
  ReceiptTypeCount,
  ResidentWithBalance,
} from './api';
import { ReportingPhoenixApi } from './api';
import { format } from 'date-fns';

const ApiContext = createContext<IReportingPhoenixApi | undefined>(undefined);
const {
  useApi: useReportingPhoenixApi,
  ApiProvider: ReportingPhoenixApiProvider,
} = makeApiProvider({
  name: 'ReportingPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new ReportingPhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useReportingPhoenixApi,
  ReportingPhoenixApiProvider,
  ApiContext as ReportingPhoenixApiContext,
};

export type {
  AvailableFundsDetails,
  BalanceChange,
  BookkeepingChange,
  CashListBalanceChange,
  FinanceResult,
  GetReportingOverviewParams,
  GetReportingOverviewResult,
  GetPayoutsResult,
  GetPayoutsParams,
  GetPayinsResult,
  GetPayinsParams,
  PayinFilters,
  PayinSortColumn,
  PayinStatusFilter,
  GetPayoutReconciliationResult,
  GetPayoutReconciliationParams,
  PayoutReconciliationEntry,
  PayoutType,
  GetDashboardResult,
  GetDashboardParams,
  ReceiptStatusCount,
  ReceiptTypeCount,
  ResidentWithBalance,
};

export const getFinanceKey = (nursingHomeId: string | undefined): QueryKey => [
  'GET_FINANCE',
  nursingHomeId,
];

export const useGetFinancePhoenix = ({
  nursingHomeId,
  options,
}: {
  nursingHomeId: string | undefined;
  options?: Omit<
    UseQueryOptions<FinanceResult, unknown>,
    'queryFn' | 'queryKey'
  >;
}): UseQueryResult<FinanceResult, unknown> => {
  return useApiQuery(
    useReportingPhoenixApi,
    getFinanceKey(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('Missing nursing home ID');
      }
      return api.getFinance({
        params: {
          nursing_home_id: nursingHomeId,
        },
      });
    },
    {
      ...options,
      enabled: nursingHomeId !== undefined,
    }
  );
};

export const useGetPayoutsPhoenix = (
  params: GetPayoutsParams,
  options?: Omit<
    UseQueryOptions<GetPayoutsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetPayoutsResult, unknown> => {
  const select = useCallback((data: GetPayoutsResult) => {
    return {
      ...data,
      data: data.data.map((payout) => ({
        ...payout,
        inserted_at: new Date(payout.inserted_at),
        transferred: payout.transferred
          ? new Date(payout.transferred)
          : undefined,
      })),
    };
  }, []);
  return useApiQuery(
    useReportingPhoenixApi,
    ['GET_PAYOUTS_PHOENIX', params.params],
    (api) => api.getPayouts(params),
    {
      ...(options ?? {}),
      select,
    }
  );
};

export const useGetPayoutReconciliationPhoenix = (
  params: GetPayoutReconciliationParams,
  options?: Omit<
    UseQueryOptions<GetPayoutReconciliationResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetPayoutReconciliationResult, unknown> => {
  // Fetched on demand via refetch() while disabled, which useApiQuery rejects.
  return useApiQueryNoRefetchOverride(
    useReportingPhoenixApi,
    ['GET_PAYOUT_RECONCILIATION_PHOENIX', params.params],
    (api) => api.getPayoutReconciliation(params),
    options
  );
};

export const useGetPayinsPhoenix = (
  params: GetPayinsParams,
  options?: Omit<
    UseQueryOptions<GetPayinsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetPayinsResult, unknown> => {
  const select = useCallback((data: GetPayinsResult) => {
    return {
      ...data,
      data: data.data.map((payin) => ({
        ...payin,
        action_timestamp: new Date(payin.action_timestamp),
        updated_at: new Date(payin.updated_at),
        charge_date: new Date(payin.charge_date),
      })),
    };
  }, []);
  return useApiQuery(
    useReportingPhoenixApi,
    ['GET_PAYINS_PHOENIX', params.params],
    (api) => api.getPayins(params),
    {
      ...(options ?? {}),
      select,
    }
  );
};

export const getDashboardKey = (
  nursingHomeId: string | undefined,
  dateString: string
): QueryKey => ['GET_DASHBOARD', nursingHomeId, dateString];

export const useGetDashboardPhoenix = ({
  nursingHomeId,
  month,
  options,
}: {
  nursingHomeId: string | undefined;
  month?: Date;
  options?: Omit<
    UseQueryOptions<GetDashboardResult, unknown>,
    'queryFn' | 'queryKey'
  >;
}): UseQueryResult<GetDashboardResult, unknown> => {
  const select = useCallback((data: GetDashboardResult) => {
    const transformedData = {
      ...data,
      data: {
        ...data.data,
        negative_balance: {
          ...data.data.negative_balance,
          residents: data.data.negative_balance.residents.map((resident) => ({
            ...resident,
            due_date: new Date(resident.due_date),
          })),
        },
        balance_below_threshold: {
          ...data.data.balance_below_threshold,
          residents: data.data.balance_below_threshold.residents.map(
            (resident) => ({
              ...resident,
              due_date: new Date(resident.due_date),
            })
          ),
        },
        date_range: {
          ...data.data.date_range,
          from_date: new Date(data.data.date_range.from_date),
          to_date: new Date(data.data.date_range.to_date),
        },
      },
    };
    return transformedData;
  }, []);

  const dateString = month
    ? format(month, 'yyyy-MM')
    : format(new Date(), 'yyyy-MM');

  return useApiQuery(
    useReportingPhoenixApi,
    getDashboardKey(nursingHomeId, dateString),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('Missing nursing home ID');
      }
      return api.getDashboard({
        params: {
          nursing_home_id: nursingHomeId,
          month: dateString,
        },
      });
    },
    {
      ...(options ?? {}),
      enabled: nursingHomeId !== undefined,
      select,
    }
  );
};

export const useGetReportingOverviewPhoenix = ({
  nursingHomeId,
  from,
  to,
}: {
  nursingHomeId: string | undefined;
  from: string | undefined;
  to: string | undefined;
}): UseQueryResult<GetReportingOverviewResult, unknown> => {
  return useApiQuery(
    useReportingPhoenixApi,
    ['GET_REPORTING_OVERVIEW', nursingHomeId, from, to],
    (api) => {
      if (!nursingHomeId || !from || !to) {
        throw new Error('Missing nursing home ID or date range');
      }
      return api.getOverview({
        params: { nursing_home_id: nursingHomeId, from, to },
      });
    },
    {
      enabled: nursingHomeId !== undefined && !!from && !!to,
    }
  );
};

export interface NursingHomeReportingData {
  nursing_home_id: string;
  overview: GetReportingOverviewResult['data'];
  bookkeeping_changes: BookkeepingChange[];
}

// One overview and one bookkeeping request per nursing home. Fine for a draft,
// but slow for legal entities with many homes.
export const useGetNursingHomesReportingPhoenix = ({
  nursingHomeIds,
  from,
  to,
  cashListName,
}: {
  nursingHomeIds: string[];
  from: string | undefined;
  to: string | undefined;
  cashListName: string;
}): UseQueryResult<NursingHomeReportingData[], unknown> => {
  return useApiQuery(
    useReportingPhoenixApi,
    ['GET_NURSING_HOMES_REPORTING', nursingHomeIds, from, to, cashListName],
    (api) => {
      if (!from || !to) {
        throw new Error('Missing date range');
      }
      return Promise.all(
        nursingHomeIds.map(async (nursingHomeId) => {
          const [overview, bookkeepingChanges] = await Promise.all([
            api.getOverview({
              params: { nursing_home_id: nursingHomeId, from, to },
            }),
            api.getBookkeepingChanges({
              params: {
                nursing_home_id: nursingHomeId,
                period_start: from,
                period_end: to,
                cash_list_name: cashListName,
              },
            }),
          ]);
          return {
            nursing_home_id: nursingHomeId,
            overview: overview.data,
            bookkeeping_changes: bookkeepingChanges.data,
          };
        })
      );
    },
    {
      enabled: nursingHomeIds.length > 0 && !!from && !!to,
    }
  );
};
